// 261010 P9D D7 — khối cảnh báo ở khung 8 bước: chip «Dàn cảnh» + sổ liền mạch (bước 3), «lệch bộ đồ» + lint prompt (bước 7). Logic thuần ở `workflowWarnUtils.ts`.
// Mọi nút chỉ gọi route CODE / route cũ có sẵn: refresh-continuity (không AI), authorEventPrompts({rebuildEventIds}) như UI cũ N5, GET lint. Không gọi route trả phí mới.
import React, { useState } from 'react';
import { Button, Modal, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { EventPlan, FilmEvent, FilmRunningJob } from '../../types/film';
import { apiErrorText } from './apiErrorText';
import { continuityWarningsOf, ledgerViewOf, lintForEventOf, lookMismatchOf, stagingChipOf, staleNoteOf, type LintFindingLite } from './workflowWarnUtils';
import { needsConfirm } from './workflowConfirmPolicy';

/** Chip «Dàn cảnh x/y» đầu bước 3 — đếm theo MỌI video của plan. */
export const StagingChip: React.FC<{ detail: EventPlan | null }> = ({ detail }) => {
  if (!detail) return null;
  const chip = stagingChipOf(detail.scenes.flatMap((s) => s.events), detail.plan.stagingVersion);
  if (!chip) return null;
  return <span className={`wf-chip ${chip.tone}`} data-testid="wf-staging-chip" title="Số video đã có dàn cảnh (staging) READY / tổng số video của plan.">{chip.label}</span>;
};

/** Sổ liền mạch của video: cảnh báo + nút «Làm mới sổ liền mạch» (CODE, không AI; hỏi trước vì dòng đổi có thể làm clip cũ thành cũ). */
export const ContinuityBlock: React.FC<{ ev: FilmEvent; jobs: Array<Pick<FilmRunningJob, 'type'>>; onActionDone: () => void }> = ({ ev, jobs, onActionDone }) => {
  const [busy, setBusy] = useState(false);
  const warns = continuityWarningsOf(ev);
  const ledger = ledgerViewOf(ev);
  const planBusy = jobs.some((j) => ['FILM_AUTHOR_PROMPTS', 'FILM_CONTINUITY', 'FILM_SCRIPT_PASSES'].includes(j.type));
  // U9: làm mới sổ liền mạch (code, không AI) = chạy lại bước Prompt ⇒ chạy ngay.
  const doRefresh = async () => {
    setBusy(true);
    try { await filmApi.refreshEventContinuity(ev.id); message.success('Đã làm mới sổ liền mạch.'); }
    catch (e) { message.error(apiErrorText(e, 'Làm mới sổ liền mạch')); }
    finally { setBusy(false); onActionDone(); }
  };
  const refresh = () => (!needsConfirm('PROMPT', 'RERUN') ? void doRefresh() : Modal.confirm({
    title: 'Làm mới sổ liền mạch của video này?',
    content: 'Máy sửa dòng [Continuity] trong prompt đã lưu bằng code (không AI). Dòng đổi thì clip đã gen từ prompt cũ có thể thành cũ.',
    okText: 'Làm mới', cancelText: 'Huỷ',
    onOk: async () => {
      setBusy(true);
      try { await filmApi.refreshEventContinuity(ev.id); message.success('Đã làm mới sổ liền mạch.'); }
      catch (e) { message.error(apiErrorText(e, 'Làm mới sổ liền mạch')); }
      finally { setBusy(false); onActionDone(); }
    },
  }));
  return (
    <section className="wf-continuity" data-testid="wf-continuity" style={{ margin: '8px 0' }}>
      <h3>Liền mạch</h3>
      {warns.length === 0
        ? <p className="muted" style={{ margin: '4px 0' }} data-testid="wf-continuity-none">Không có cảnh báo liền mạch.</p>
        : <ul className="wf-list" data-testid="wf-continuity-list">{warns.map((w) => <li key={w}>{w}</li>)}</ul>}
      <h3 style={{ marginTop: 10 }}>Sổ liền mạch (trạng thái sau video)</h3>
      {ledger ? (
        <div style={{ fontSize: 12.5 }} data-testid="wf-ledger">
          {ledger.characters.map((c) => (
            <div key={c.name} style={{ marginBottom: 2 }}>
              <b>{c.name}</b>: {c.position || '?'}{c.holding.length ? ` · đang cầm: ${c.holding.join(', ')}` : ''}{c.mood ? ` · ${c.mood}` : ''}
            </div>))}
          {ledger.setting ? <div className="muted" style={{ marginTop: 4 }}>🏙️ {ledger.setting.location || '?'} · {ledger.setting.timeOfDay || '?'}</div> : null}
        </div>
      ) : <p className="muted" style={{ margin: '4px 0' }} data-testid="wf-ledger-none">Chưa có sổ liền mạch cho video này — chạy chặng 2 (Máy tự chạy) để dựng.</p>}
      <Button size="small" loading={busy} style={{ marginTop: 6 }} disabled={planBusy || !ev.authorPrompt} title={planBusy ? 'Plan đang dựng prompt / soát liền mạch — đợi xong.' : !ev.authorPrompt ? 'Video chưa có prompt.' : undefined} onClick={refresh} data-testid="wf-continuity-refresh">Làm mới sổ liền mạch</Button>
    </section>
  );
};

/** Bước 7: video đã cũ vì gì + lệch bộ đồ của video. */
export const PromptStaleLine: React.FC<{ ev: FilmEvent; detail: EventPlan | null }> = ({ ev, detail }) => {
  const stale = staleNoteOf(ev.staleStages);
  const lm = lookMismatchOf(detail?.plan, ev);
  if (!stale && !lm.thisEvent) return null;
  return (
    <div style={{ marginBottom: 8 }} data-testid="wf-prompt-stale">
      {stale ? <span className="wf-chip warn" title={stale} data-testid="wf-prompt-stale-chip">đã cũ</span> : null}
      {lm.thisEvent ? <span className="wf-chip warn" style={{ marginLeft: 6 }} title={`Prompt đang ghim bộ đồ CŨ cho: ${lm.names.join(', ')} — bảng gán cảnh đã đổi. Bấm «Dựng lại prompt».`} data-testid="wf-prompt-lookmismatch">lệch bộ đồ</span> : null}
      {stale ? <span className="muted" style={{ marginLeft: 8, fontSize: 12.5 }}>{stale}</span> : null}
    </div>
  );
};

/** Bước 7 (cấp plan): «lệch bộ đồ (N)» — CHỈ HIỆN + dẫn tới từng video. KHÔNG có nút dựng lại riêng: dựng prompt đi qua runner của bước 7 (nút chạy ở đầu bước) hoặc «Dựng lại prompt» của từng video; không gọi route legacy `authorEventPrompts` (vòng qua sổ L1). */
export const LookMismatchBar: React.FC<{ detail: EventPlan | null; onOpenEvent: (eventId: string) => void }> = ({ detail, onOpenEvent }) => {
  const lm = lookMismatchOf(detail?.plan, null);
  if (lm.count === 0) return null;
  const orderOf = new Map((detail?.scenes.flatMap((s) => s.events) ?? []).map((e) => [e.id, e.eventOrder] as [string, number]));
  return (
    <div className="wf-alert WARN" role="status" data-testid="wf-lookmismatch-bar">
      <b>Lệch bộ đồ ({lm.count})</b> <span className="muted">— {lm.count} video có bộ đồ trong prompt khác bảng gán cảnh hiện tại. Không chặn gen. Dựng lại bằng nút chạy của bước này, hoặc «Dựng lại prompt» ở từng video:</span>
      <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {lm.ids.map((id) => <Button key={id} size="small" onClick={() => onOpenEvent(id)} data-testid="wf-lookmismatch-open">Mở video {orderOf.get(id) ?? '?'}</Button>)}
      </div>
    </div>
  );
};

/** Dưới ô prompt: lint (câu dài / tràn nhịp…) lọc theo video — GET lint, 0 USD, bấm mới chạy. */
export const PromptLintBlock: React.FC<{ planId: string; eventOrder: number }> = ({ planId, eventOrder }) => {
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState<LintFindingLite[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try { const r = await filmApi.lintPlan(planId); setRows(lintForEventOf(r.findings as LintFindingLite[], eventOrder)); }
    catch (e) { setErr(apiErrorText(e, 'Kiểm lỗi')); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 8 }} data-testid="wf-prompt-lint">
      <Button size="small" loading={busy} onClick={() => { void run(); }} data-testid="wf-prompt-lint-run">Kiểm lỗi video này</Button>
      <span className="faint" style={{ marginLeft: 8, fontSize: 12 }}>Máy kiểm bằng code (không AI, không tốn phí).</span>
      {err ? <p className="wf-alert ERROR" role="alert">{err} <button type="button" className="wf-btn" onClick={() => { void run(); }}>Thử lại</button></p> : null}
      {rows && rows.length === 0 ? <p className="muted" style={{ margin: '6px 0 0' }} data-testid="wf-prompt-lint-ok">Không có lỗi hay cảnh báo cho video này.</p> : null}
      {rows && rows.length > 0 ? <ul className="wf-list" data-testid="wf-prompt-lint-list">{rows.map((f, i) => <li key={`${f.code}-${i}`}><span className={`wf-chip ${f.severity === 'error' ? 'bad' : 'warn'}`}>{f.severity === 'error' ? 'lỗi' : 'cảnh báo'}</span> {f.message}</li>)}</ul> : null}
    </div>
  );
};
